import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// import { FormLayout } from '@core/login/components/Form';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useDevicesTestState } from './hooks/state';

import {
  STEP_TESTS,
  STEPS_CONTENT,
} from '@core/devices/constants/devicesSteps';

import PageWrapper from '@components/PageWrapper';
import ContentWrapper from '@components/ContentWrapper';
import BottomButtonsWrapper from '@components/BottomButtonsWrapper';
import ROUTES from '@constants/routes';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import { IUser } from '@interfaces/user';
import ParticipantsService from '@services/ParticipantsService';
import ProgressSteps from '@core/devices/components/DevicesTest/ProgressSteps';

const DevicesTestPage = () => {
  const { t: translate } = useTranslation();
  const { step, resetState, setStep } = useDevicesTestState();
  const { currentEvent, currentEventId, currentSkin } = useMytaverseEvent();
  const { setCurrentScreen, setUser, setUserAvatars } = useMytaverse();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');

  useEffect(() => resetState, [resetState]);
  const navigate = useNavigate();

  const stepsContent = React.useMemo(() => {
    const defaultRoom = currentEvent?.rooms.find((room) => room.isDefault);

    if (defaultRoom && !defaultRoom.isMultiPlayer) {
      return [STEPS_CONTENT[0], STEPS_CONTENT[2]];
    }

    return STEPS_CONTENT;
  }, [currentEvent]);

  const [isLastStep] = React.useMemo(() => {
    const defaultRoom = currentEvent?.rooms.find((room) => room.isDefault);

    if (defaultRoom && !defaultRoom.isMultiPlayer) {
      return [step === 1];
    }

    return [step === 3];
  }, [step, currentEvent]);

  const handleNextStep = () => {
    if (isLastStep) {
      navigate(ROUTES.EVENT_PAGE(currentEventId));
    } else {
      setStep(step + 1);
    }
  };

  const handleBackStep = async () => {
    if (step === 0) {
      if (!currentSkin) {
        const participantAvatars =
          await ParticipantsService.getParticipantAvatars();

        setUser((prevState: IUser) => ({
          ...prevState,
          avatars: participantAvatars,
        }));
        setUserAvatars(participantAvatars);
      }

      currentSkin
        ? navigate(ROUTES.SELECT_AVATAR_SKIN(currentEventId))
        : navigate(ROUTES.SELECT_AVATAR_PROVIDER(currentEventId));
    } else {
      setStep(step - 1);
    }
  };

  useEffect(() => {
    const { stepTest } = STEPS_CONTENT[step]();
    switch (stepTest) {
      case STEP_TESTS.CONNECTION: {
        setTitle(translate('devicesTest.connectionTest.title'));
        setSubtitle(translate('devicesTest.connectionTest.description'));
        setCurrentScreen(CurrentScreenEnum.DEVICES_TEST_CONNECTION);
        break;
      }
      case STEP_TESTS.MICROPHONE: {
        setTitle(translate('devicesTest.microTest.title'));
        setSubtitle(translate('devicesTest.microTest.description'));
        setCurrentScreen(CurrentScreenEnum.DEVICES_TEST_MICROPHONE);
        break;
      }
      case STEP_TESTS.SPEAKER: {
        setTitle(translate('devicesTest.speakerTest.title'));
        setSubtitle(translate('devicesTest.speakerTest.description'));
        setCurrentScreen(CurrentScreenEnum.DEVICES_TEST_SPEAKER);
        break;
      }
      case STEP_TESTS.WEBCAM: {
        setTitle(translate('devicesTest.webcamTest.title'));
        setSubtitle(translate('devicesTest.webcamTest.description'));
        setCurrentScreen(CurrentScreenEnum.DEVICES_TEST_WEBCAM);
        break;
      }
    }
  }, [step]);

  const BottomElement = (
    <BottomButtonsWrapper
      leftButtonText={translate('back')}
      leftButtonClick={handleBackStep}
      rightButtonText={translate('continue')}
      rightButtonClick={handleNextStep}
    />
  );

  return (
    <>
      <ProgressSteps />
      <PageWrapper bottomElement={BottomElement} title={title}>
        <ContentWrapper subtitle={subtitle} forceKeepSubtitle>
          {stepsContent[step]().content}
        </ContentWrapper>
      </PageWrapper>
    </>
  );
};

export default DevicesTestPage;
